export interface ThemeColorSeed {
  white: string;
  black: string;
  fallbackText: string;
  dangerLight: string;
  dangerDark: string;
  successLight: string;
  successDark: string;
  warningLight: string;
  warningDark: string;
}

export interface ThemeRuntimeColors {
  background: string;
  chrome: string;
  navigation: string;
  surface: string;
  surfaceRaised: string;
  surfaceSoft: string;
  headerText: string;
  navText: string;
  cardBorder: string;
  primary: string;
  primarySoft: string;
  primarySoftStrong: string;
  primaryText: string;
  hoverSurface: string;
  pageText: string;
  listText: string;
  listMutedText: string;
  mutedText: string;
  controlSurface: string;
  controlIcon: string;
  border: string;
  danger: string;
  dangerInk: string;
  dangerHover: string;
  dangerActive: string;
  success: string;
  warning: string;
  colorScheme: 'dark' | 'light';
  linkColor: string;
  paperSurface: string;
  paperEdge: string;
  paperInk: string;
  paperMuted: string;
  paperSepia: string;
  paperSepiaInk: string;
  switchOffStart: string;
  switchOffEnd: string;
  switchOffEdge: string;
  switchThumb: string;
  switchThumbEdge: string;
  switchOnStart: string;
  switchOnEnd: string;
  switchOnEdge: string;
  selectionBackground: string;
}

function hexToRgb(hex: string): [number, number, number] | null {
  const normalized = hex.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  return [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16)) as [number, number, number];
}

function rgbToHex([red, green, blue]: [number, number, number]): string {
  return `#${[red, green, blue].map((channel) => Math.round(Math.max(0, Math.min(255, channel))).toString(16).padStart(2, '0')).join('')}`;
}

export function mixHex(first: string, second: string, secondWeight: number): string {
  const firstRgb = hexToRgb(first);
  const secondRgb = hexToRgb(second);
  if (!firstRgb || !secondRgb) return first;
  const weight = Math.max(0, Math.min(1, secondWeight));
  return rgbToHex(firstRgb.map((channel, index) => channel * (1 - weight) + secondRgb[index] * weight) as [number, number, number]);
}

function luminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgb
    .map((channel) => channel / 255)
    .map((channel) => channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4)
    .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index], 0);
}

export function contrastRatio(first: string, second: string): number {
  const firstLuminance = luminance(first);
  const secondLuminance = luminance(second);
  if (firstLuminance === null || secondLuminance === null) return 1;
  return (Math.max(firstLuminance, secondLuminance) + 0.05) / (Math.min(firstLuminance, secondLuminance) + 0.05);
}

export function readableTextColor(hex: string, seed: ThemeColorSeed): string {
  const value = luminance(hex);
  if (value === null) return seed.fallbackText;
  return value < 0.179 ? seed.white : seed.black;
}

function warmDark(seed: ThemeColorSeed): string {
  return mixHex(seed.black, seed.fallbackText, 0.34);
}

function warmLight(seed: ThemeColorSeed): string {
  return mixHex(seed.white, seed.fallbackText, 0.1);
}

function colorTemperature(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  // 正值偏暖（红/黄），负值偏冷（蓝/青）。只用于选择前景倾向，最终仍由对比度兜底。
  return (rgb[0] * 0.7 + rgb[1] * 0.3 - rgb[2]) / 255;
}

function ensureReadableColor(foreground: string, background: string, seed: ThemeColorSeed, minimum = 4.5): string {
  if (contrastRatio(foreground, background) >= minimum) return foreground;
  const readableTarget = readableTextColor(background, seed);
  for (let weight = 0.05; weight <= 1; weight += 0.05) {
    const candidate = mixHex(foreground, readableTarget, weight);
    if (contrastRatio(candidate, background) >= minimum) return candidate;
  }
  return readableTarget;
}

/**
 * 自定义主题的正文/图标前景：优先保留一点暖色，抵消冷色背景带来的视觉发硬，
 * 但任何情况下都以实际对比度为准。预设主题不走这里，避免改变既有视觉性格。
 */
function readableWarmColor(background: string, seed: ThemeColorSeed, minimum = 4.5): string {
  const isCoolBackground = colorTemperature(background) < -0.04;
  const lightCandidate = warmLight(seed);
  const darkCandidate = warmDark(seed);
  const candidates = isCoolBackground
    ? [readableTextColor(background, seed) === seed.white ? lightCandidate : darkCandidate, seed.white, seed.black]
    : [readableTextColor(background, seed) === seed.white ? seed.white : darkCandidate, seed.black, seed.white];
  const readable = candidates.find((candidate) => contrastRatio(candidate, background) >= minimum);
  return readable ?? ensureReadableColor(candidates[0], background, seed, minimum);
}

/** 只负责颜色推导，不读取 DOM，也不写 CSS；主题运行时只消费这个结果。 */
export function deriveThemeColors(palette: [string, string, string], seed: ThemeColorSeed): ThemeRuntimeColors {
  const [background, primaryColor, accentColor] = palette;
  const primary = accentColor || primaryColor;
  const primaryText = readableTextColor(primary, seed);
  const isDarkPrimary = primaryText === seed.white;
  const surface = isDarkPrimary ? mixHex(primary, seed.black, 0.72) : mixHex(primary, seed.white, 0.9);
  const surfaceRaised = isDarkPrimary ? mixHex(surface, seed.white, 0.14) : mixHex(surface, seed.black, 0.08);
  const controlSurface = isDarkPrimary ? mixHex(surface, primaryText, 0.08) : mixHex(surface, primaryText, 0.04);
  const hoverSurface = isDarkPrimary ? mixHex(surface, primaryText, 0.14) : mixHex(surface, primaryText, 0.08);
  const chrome = isDarkPrimary ? mixHex(primary, seed.black, 0.22) : mixHex(primary, seed.white, 0.2);
  const navigation = isDarkPrimary ? mixHex(primary, seed.black, 0.34) : mixHex(primary, seed.white, 0.08);
  const border = isDarkPrimary ? mixHex(surface, primaryText, 0.42) : mixHex(primaryText, surface, 0.72);
  const surfaceText = readableWarmColor(surface, seed);
  // 正文大多落在卡片/面板上；不要只根据页面背景推导，否则深色背景叠浅色卡片时会出现白字白底。
  const pageText = surfaceText;
  // 信件列表直接浮在页面背景上，必须按页面背景单独兜底，不能复用图片遮罩文字。
  const listText = ensureReadableColor(surfaceText, background, seed);
  const listMutedText = ensureReadableColor(mixHex(listText, background, 0.38), background, seed);
  const mutedText = ensureReadableColor(mixHex(surfaceText, surface, isDarkPrimary ? 0.42 : 0.48), surface, seed, 4.5);
  const controlIcon = ensureReadableColor(mixHex(surfaceText, surface, isDarkPrimary ? 0.28 : 0.34), controlSurface, seed, 3);
  const danger = isDarkPrimary ? seed.dangerLight : seed.dangerDark;
  const dangerInk = readableTextColor(danger, seed);
  const success = isDarkPrimary ? seed.successLight : seed.successDark;
  const warning = isDarkPrimary ? seed.warningLight : seed.warningDark;
  const paperSurface = mixHex(seed.white, surface, isDarkPrimary ? 0.08 : 0.12);
  const paperEdge = mixHex(paperSurface, border, 0.42);
  const paperInk = readableTextColor(paperSurface, seed);
  const paperMuted = mixHex(paperInk, paperSurface, 0.48);
  const paperSepia = mixHex(paperSurface, primary, 0.08);
  const paperSepiaInk = readableTextColor(paperSepia, seed);
  const primarySoft = mixHex(primary, surface, isDarkPrimary ? 0.56 : 0.82);

  return {
    background, chrome, navigation, surface, surfaceRaised,
    surfaceSoft: mixHex(surface, primaryText, isDarkPrimary ? 0.14 : 0.08),
    headerText: readableTextColor(chrome, seed),
    navText: readableTextColor(navigation, seed),
    cardBorder: border,
    primary, primarySoft, primarySoftStrong: mixHex(primary, surface, isDarkPrimary ? 0.36 : 0.68),
    primaryText, hoverSurface, pageText, listText, listMutedText, mutedText, controlSurface, controlIcon, border,
    danger, dangerInk,
    dangerHover: mixHex(danger, dangerInk, 0.12),
    dangerActive: mixHex(danger, dangerInk, 0.24),
    success, warning,
    colorScheme: isDarkPrimary ? 'dark' : 'light',
    linkColor: ensureReadableColor(ensureReadableColor(primary, surface, seed), primarySoft, seed),
    paperSurface, paperEdge, paperInk, paperMuted, paperSepia, paperSepiaInk,
    switchOffStart: mixHex(primaryText, surface, 0.66),
    switchOffEnd: mixHex(primaryText, surface, 0.42),
    switchOffEdge: mixHex(primaryText, border, 0.28),
    switchThumb: mixHex(surface, primaryText, 0.12),
    switchThumbEdge: mixHex(primaryText, surface, 0.48),
    switchOnStart: mixHex(primary, primaryText, 0.12),
    switchOnEnd: mixHex(primary, primaryText, 0.3),
    switchOnEdge: mixHex(primary, primaryText, 0.18),
    selectionBackground: mixHex(primary, surface, 0.4),
  };
}
